import { defaultBrand } from '@/domain/storefront/storefront-colors';
import type { IRestaurant } from '@/interfaces/IRestaurant';
import type {
  IFocalPoint,
  IStyleCatalogPreset,
  IStyleMode,
  IUpdateDesignBody,
} from '@/interfaces/IStorefrontStyle';
import { clampFocal } from './focal-point';

export const DEFAULT_PRESET_ID = 'moderno';
const BRAND_HEX = /^#[0-9a-f]{6}$/i;
const MODES: ReadonlyArray<IStyleMode> = ['light', 'dark', 'system'];

/**
 * What the «Diseño y estilo» page edits. `bannerFocal` (CON-1 `IFocalPoint`,
 * integers 0–100 in % of the image) is null when the store has no banner.
 */
export interface IDesignDraft {
  preset: string;
  /** `#rrggbb`, lowercase. */
  brand: string;
  mode: IStyleMode;
  bannerFocal: IFocalPoint | null;
}

export function isBrandHex(value: string): boolean {
  return BRAND_HEX.test(value.trim());
}

export function normalizeBrand(hex: string): string {
  return hex.trim().toLowerCase();
}

export function isStyleMode(value: string): value is IStyleMode {
  return MODES.some((mode) => mode === value);
}

/**
 * The saved look as a draft. `storefrontStyle` is the API's resolution (saved
 * → legacy → logo colour → hashed hue, D-TH-2); without it (an API older than
 * this release) the never-themed default is rebuilt locally: logo colour →
 * hashed hue (`defaultBrand`).
 */
export function draftFromRestaurant(
  restaurant: Pick<
    IRestaurant,
    'name' | 'banner' | 'storefrontStyle' | 'bannerMeta' | 'logoColor'
  >
): IDesignDraft {
  const style = restaurant.storefrontStyle;
  const meta = restaurant.bannerMeta;
  return {
    preset: style?.preset ?? DEFAULT_PRESET_ID,
    brand: normalizeBrand(
      style?.brand ??
        defaultBrand({ logoColor: restaurant.logoColor, name: restaurant.name })
    ),
    mode: style?.mode ?? 'system',
    bannerFocal: restaurant.banner
      ? {
          x: clampFocal(meta?.focalX ?? 50),
          y: clampFocal(meta?.focalY ?? 50),
        }
      : null,
  };
}

export function cloneDraft(draft: IDesignDraft): IDesignDraft {
  return {
    ...draft,
    bannerFocal: draft.bannerFocal ? { ...draft.bannerFocal } : null,
  };
}

/** A style pick: the next draft and the owner's mode to give back later. */
export interface IPresetChoice {
  draft: IDesignDraft;
  /**
   * The owner's mode from before a preset with its own mode replaced it; null
   * when none did. Page state: never part of the draft, so never saved.
   */
  modeBeforePreset: IStyleMode | null;
}

/**
 * Picks a preset (D-TH-7, amended by the owner feedback of 2026-11-01). The
 * selected preset again changes nothing. A preset with its own mode (Noche →
 * Oscuro) sets it and remembers the owner's mode from before the first such
 * preset; a preset that follows the phone gives that mode back and forgets
 * it. With nothing remembered (the store opened on Noche) the mode stays.
 */
export function applyPreset(
  draft: IDesignDraft,
  preset: IStyleCatalogPreset,
  modeBeforePreset: IStyleMode | null
): IPresetChoice {
  const next = cloneDraft(draft);
  if (preset.id === draft.preset) return { draft: next, modeBeforePreset };
  if (preset.defaultMode !== 'system')
    return {
      draft: { ...next, preset: preset.id, mode: preset.defaultMode },
      modeBeforePreset: modeBeforePreset ?? draft.mode,
    };
  return {
    draft: { ...next, preset: preset.id, mode: modeBeforePreset ?? draft.mode },
    modeBeforePreset: null,
  };
}

export function isDirty(saved: IDesignDraft, draft: IDesignDraft): boolean {
  if (saved.preset !== draft.preset || saved.mode !== draft.mode) return true;
  if (normalizeBrand(saved.brand) !== normalizeBrand(draft.brand)) return true;
  const a = saved.bannerFocal;
  const b = draft.bannerFocal;
  if (!a || !b) return a !== b;
  return a.x !== b.x || a.y !== b.y;
}

/** The CON-4 body; `bannerFocal` only when the store has a banner (the API answers 400 otherwise). */
export function toSavePayload(draft: IDesignDraft): IUpdateDesignBody {
  const style = {
    preset: draft.preset,
    brand: normalizeBrand(draft.brand),
    mode: draft.mode,
  };
  return draft.bannerFocal
    ? { style, bannerFocal: { x: draft.bannerFocal.x, y: draft.bannerFocal.y } }
    : { style };
}
